import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Navigation
    'nav.features': 'Features',
    'nav.pricing': 'Pricing',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.signin': 'Sign In',
    'nav.signup': 'Sign Up',
    
    // Landing Page
    'landing.hero.title': 'Next-Generation Photo Gallery Platform',
    'landing.hero.subtitle': 'Professional photo hosting, client galleries, and e-commerce solutions for photographers worldwide',
    'landing.hero.cta': 'Start Free Trial',
    'landing.hero.learn': 'Learn More',
    
    // Features
    'features.title': 'Powerful Features for Modern Photographers',
    'features.gallery.title': 'Smart Gallery Management',
    'features.gallery.desc': 'AI-powered organization with facial recognition and scene detection',
    'features.editing.title': 'Advanced Photo Editing',
    'features.editing.desc': 'Professional-grade editing tools with LUT support and custom templates',
    'features.booking.title': 'Booking Management',
    'features.booking.desc': 'Streamlined client booking with calendar integration and automated workflows',
    
    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.upload': 'Upload',
    'common.download': 'Download',
  },
  es: {
    // Navigation
    'nav.features': 'Características',
    'nav.pricing': 'Precios',
    'nav.about': 'Acerca de',
    'nav.contact': 'Contacto',
    'nav.signin': 'Iniciar Sesión',
    'nav.signup': 'Registrarse',
    
    // Landing Page
    'landing.hero.title': 'Plataforma de Galería de Fotos de Nueva Generación',
    'landing.hero.subtitle': 'Alojamiento profesional de fotos, galerías de clientes y soluciones de comercio electrónico para fotógrafos de todo el mundo',
    'landing.hero.cta': 'Iniciar Prueba Gratuita',
    'landing.hero.learn': 'Saber Más',
  },
  fr: {
    // Navigation
    'nav.features': 'Fonctionnalités',
    'nav.pricing': 'Tarifs',
    'nav.about': 'À propos',
    'nav.contact': 'Contact',
    'nav.signin': 'Se connecter',
    'nav.signup': 'S\'inscrire',
    
    // Landing Page
    'landing.hero.title': 'Plateforme de Galerie Photo Nouvelle Génération',
    'landing.hero.subtitle': 'Hébergement photo professionnel, galeries clients et solutions e-commerce pour photographes du monde entier',
    'landing.hero.cta': 'Commencer l\'Essai Gratuit',
    'landing.hero.learn': 'En Savoir Plus',
  }
};

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
];

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('tasveer_language');
    if (savedLanguage && languages.find(lang => lang.code === savedLanguage)) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const changeLanguage = (languageCode) => {
    setCurrentLanguage(languageCode);
    localStorage.setItem('tasveer_language', languageCode);
  };

  const t = (key) => {
    return translations[currentLanguage]?.[key] || translations.en[key] || key;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    languages,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};